import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { AsyncButton, Button, FormInput, Modal, Message } from '@getro/rombo';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { emailRegEx } from '../../../helpers/regExs';

export const InviteAdminModal = ({
  isSubmitting,
  onAdminInvite,
  companyName,
  modalOpen,
  setModalOpen,
  formRef,
  apiError,
  setApiError,
}) => {
  const inviteAdminFormikSchema = Yup.object().shape({
    firstName: Yup.string().label('First name').required('Required'),
    lastName: Yup.string().label('Last name'),
    email: Yup.string().trim().matches(emailRegEx, 'Not a valid email').required('Required'),
  });
  const inviteAdminInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
  };

  return (
    <Modal
      title="Invite team members"
      isOpen={modalOpen}
      onCancel={() => {
        setModalOpen(false);
        setApiError('');
      }}
      actions={
        <Flex justifyContent={['flex-end']} sx={{ gap: '8px' }}>
          <Button
            onClick={() => {
              setModalOpen(false);
              setApiError('');
            }}
            variant="secondary"
          >
            Cancel
          </Button>
          <AsyncButton
            loading={isSubmitting}
            data-testid="invite-admin-button"
            onClick={() => {
              if (formRef.current) {
                formRef.current.handleSubmit();
              }
            }}
          >
            Send invite
          </AsyncButton>
        </Flex>
      }
      data-testid="invite-admin"
    >
      <Box width={1}>
        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={inviteAdminInitialValues}
          validationSchema={inviteAdminFormikSchema}
          onSubmit={onAdminInvite}
        >
          {({ values, errors, setFieldValue }) => (
            <Form>
              <Flex flexDirection="column" sx={{ gap: '24px' }}>
                <Text fontSize="14px" fontWeight="400" lineHeight="1.4">
                  Invite founders, hiring managers, or sales leads at {companyName}.
                </Text>
                <Field
                  required
                  name="firstName"
                  error={values.firstName && errors.firstName}
                  value={values.firstName}
                  type="text"
                  label="Name"
                  component={FormInput}
                  labelExtra="Required"
                />
                <Field
                  name="lastName"
                  error={errors.lastName}
                  value={values.lastName}
                  type="text"
                  label="Last name"
                  component={FormInput}
                />
                <Field
                  required
                  name="email"
                  error={values.email && errors.email}
                  value={values.email}
                  type="text"
                  label="Email"
                  labelExtra="Required"
                  component={FormInput}
                  onChange={(e) => {
                    setFieldValue('email', e.target.value);
                    if (apiError) {
                      setApiError('');
                    }
                  }}
                />
                {apiError && (
                  <Message mt="16px" type="error" content={<div dangerouslySetInnerHTML={{ __html: apiError }} />} />
                )}

                <Button variant="variants.srOnly" type="submit">
                  Send invite
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

InviteAdminModal.propTypes = {
  onAdminInvite: PropTypes.func.isRequired,
  companyName: PropTypes.string.isRequired,
  modalOpen: PropTypes.bool,
  setModalOpen: PropTypes.func.isRequired,
  formRef: PropTypes.object,
  isSubmitting: PropTypes.bool,
  apiError: PropTypes.string,
  setApiError: PropTypes.func.isRequired,
};

InviteAdminModal.defaultProps = {
  modalOpen: false,
  isSubmitting: false,
  formRef: null,
  apiError: '',
};
